import React from "react";
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Popover,
} from "@material-ui/core";
import { ArrowDropDown as ArrowDropDownIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { UserContext } from "../contexts/UserContext";
import { buildUser } from "../utils/user";
import { teamLabel } from "../utils/team";
import axiosConfig from "../restApi/axiosConfig";

const useStyles = makeStyles((theme) => ({
  // Matches Tab's own label styling exactly (Tab uses this same
  // typography.button variant internally) - the only thing that
  // should read as different from the other header items is the
  // dropdown arrow, not the text itself.
  button: {
    ...theme.typography.button,
    color: theme.palette.text.secondary,
  },
}));

// Only renders anything once someone actually belongs to more than one
// team - most members never see this at all. Modeled on SettingsMenu's
// own IconButton + Popover + List pattern, the closest existing idiom
// to a "switcher" anywhere in this app.
export default () => {
  const userContext = React.useContext(UserContext);
  const user = userContext.user;
  const classes = useStyles();
  const [anchor, setAnchor] = React.useState(null);

  if (!user.teams || user.teams.length <= 1) {
    return null;
  }

  const activeTeam = user.teams.find((t) => t.id === user.team);

  const handleSwitch = (teamId) => {
    setAnchor(null);
    axiosConfig
      .patch("/api/team/switch/", { team_id: teamId })
      .then((res) => {
        const switched = buildUser(res.data, true);
        localStorage.setItem("user", JSON.stringify(switched));
        // Role-based UI (like the Setlist Templates tab) reads straight
        // off the user object, so it would update fine with just
        // userContext.setUser(switched) - but everything else (song
        // proposals, upcoming events, the calendar, the catalog, admin)
        // is fetched independently by its own hook/container, once on
        // mount, with no reason to know the active team just changed.
        // A hard reload is the one thing that's guaranteed to refetch
        // all of it correctly, rather than teaching every one of those
        // data sources individually to watch for this. Reloads the
        // current URL rather than navigating away - whatever the new
        // team's own view of that page looks like (including "you don't
        // have access to this" for something scoped to the old team) is
        // more useful than being bounced somewhere else regardless.
        window.location.reload();
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <Button
        className={classes.button}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {activeTeam ? activeTeam.name : "Switch team"}
      </Button>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <List aria-label="Switch team">
          {user.teams.map((team) => (
            <ListItem
              key={team.id}
              button
              selected={team.id === user.team}
              onClick={() => handleSwitch(team.id)}
            >
              <ListItemText primary={teamLabel(team)} secondary={team.city} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};
